import { API_BASE_URL, LIMITED_LINK_KEY_HEADER } from './config';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  key: string;
  method?: 'GET' | 'POST';
  body?: unknown;
  signal?: AbortSignal;
}

interface ErrorBody {
  message?: string | string[];
}

// Middleware answers 401 for a wrong key, 503 when limited links are not set up on the
// environment, and a Nest `message` (string or validation array) for everything it refuses.
export function messageFor(status: number, body: ErrorBody | null): string {
  if (status === 401) return 'The access key is wrong.';
  if (body?.message) return Array.isArray(body.message) ? body.message.join(' ') : body.message;
  if (status === 503) return 'Limited links are not configured on this environment.';
  return `Something went wrong (${status}). Try again.`;
}

export async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const response = await fetch(`${API_BASE_URL}/api/tournaments/limited/${path}`, {
    method: options.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      [LIMITED_LINK_KEY_HEADER]: options.key,
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(messageFor(response.status, body), response.status);
  }
  return body as T;
}

export const isUnauthorized = (error: unknown): boolean => error instanceof ApiError && error.status === 401;
